/**
 * Forge → Editor loading page。
 * 负责展示关卡资源、数据持久化和 Monaco 编辑器加载状态。
 */

export type LevelLoadingStage = "fetch" | "normalize" | "persist" | "editor";

export interface LevelLoadingPageOptions {
  mode: "overlay" | "page";
  title: string;
  subtitle: string;
}

export interface LevelLoadingPageController {
  setStage(stage: LevelLoadingStage, detail?: string): void;
  setError(error: unknown, onRetry: () => void, onBack: () => void): void;
  setAuthRequired(onLogin: () => void, onBack: () => void): void;
  isMounted(): boolean;
  dispose(): void;
}

const STAGES: Array<{ key: LevelLoadingStage; label: string; code: string }> = [
  { key: "fetch", label: "读取关卡资源", code: "MAP / RULES / AI" },
  { key: "normalize", label: "校准战场数据", code: "SCHEMA / SPAWN / GRID" },
  { key: "persist", label: "同步训练档案", code: "ACCOUNT LEVEL STORE" },
  { key: "editor", label: "装载作战编辑器", code: "MONACO / WORKSPACE" },
];

const STAGE_PROGRESS: Record<LevelLoadingStage, number> = {
  fetch: 18,
  normalize: 45,
  persist: 72,
  editor: 92,
};

/** loading 页最少展示毫秒数，避免预加载太快导致闪屏 */
const MIN_VISIBLE_MS = 3000;

export function renderLevelLoadingPage(
  container: HTMLElement,
  options: LevelLoadingPageOptions,
): LevelLoadingPageController {
  const root = document.createElement("section");
  root.className = `level-loading-page level-loading-page--${options.mode}`;
  root.setAttribute("aria-live", "polite");
  root.innerHTML = `
    <div class="level-loading-page__frame">
      <div class="level-loading-page__eyebrow">BATTLECODE // FORGE HANDOFF</div>
      <div class="level-loading-page__title"></div>
      <div class="level-loading-page__subtitle"></div>
      <div class="level-loading-page__signal" aria-hidden="true">
        <span class="level-loading-page__signal-core"></span>
        <span class="level-loading-page__signal-line"></span>
      </div>
      <div class="level-loading-page__status">
        <span class="level-loading-page__status-label">INITIALIZING</span>
        <span class="level-loading-page__status-detail">准备装载关卡数据</span>
      </div>
      <div class="level-loading-page__progress" aria-hidden="true">
        <span></span>
      </div>
      <ol class="level-loading-page__steps">
        ${STAGES.map((stage, index) => `
          <li class="level-loading-page__step" data-stage="${stage.key}">
            <span class="level-loading-page__step-index">0${index + 1}</span>
            <span class="level-loading-page__step-copy">
              <strong>${stage.label}</strong>
              <small>${stage.code}</small>
            </span>
            <span class="level-loading-page__step-state" aria-hidden="true">○</span>
          </li>
        `).join("")}
      </ol>
      <div class="level-loading-page__error" hidden>
        <strong>LOAD ABORTED</strong>
        <p></p>
        <div class="level-loading-page__error-actions">
          <button type="button" data-action="retry">重试</button>
          <button type="button" data-action="login" hidden>去登录</button>
          <button type="button" data-action="back">返回铸剑阁</button>
        </div>
      </div>
      <div class="level-loading-page__footer">SECURE CHANNEL // NO DATA LOST</div>
    </div>
  `;

  if (options.mode === "page") {
    container.replaceChildren(root);
  } else {
    container.appendChild(root);
  }

  const title = root.querySelector<HTMLElement>(".level-loading-page__title")!;
  const subtitle = root.querySelector<HTMLElement>(".level-loading-page__subtitle")!;
  const statusLabel = root.querySelector<HTMLElement>(".level-loading-page__status-label")!;
  const statusDetail = root.querySelector<HTMLElement>(".level-loading-page__status-detail")!;
  const progress = root.querySelector<HTMLElement>(".level-loading-page__progress span")!;
  const errorPanel = root.querySelector<HTMLElement>(".level-loading-page__error")!;
  const errorText = root.querySelector<HTMLElement>(".level-loading-page__error p")!;
  const retryButton = root.querySelector<HTMLButtonElement>('[data-action="retry"]')!;
  const loginButton = root.querySelector<HTMLButtonElement>('[data-action="login"]')!;
  const backButton = root.querySelector<HTMLButtonElement>('[data-action="back"]')!;
  const stepElements = new Map(
    Array.from(root.querySelectorAll<HTMLElement>(".level-loading-page__step"))
      .map((element) => [element.dataset.stage as LevelLoadingStage, element]),
  );

  title.textContent = options.title;
  subtitle.textContent = options.subtitle;

  const createdAt = performance.now();
  let disposed = false;

  function setStage(stage: LevelLoadingStage, detail?: string): void {
    if (disposed) return;
    const stageIndex = STAGES.findIndex((item) => item.key === stage);
    for (const [index, item] of STAGES.entries()) {
      const element = stepElements.get(item.key);
      if (!element) continue;
      element.classList.toggle("is-done", index < stageIndex);
      element.classList.toggle("is-active", index === stageIndex);
      const state = element.querySelector<HTMLElement>(".level-loading-page__step-state");
      if (state) state.textContent = index < stageIndex ? "✓" : index === stageIndex ? "◆" : "○";
    }
    root.dataset.stage = stage;
    progress.style.width = `${STAGE_PROGRESS[stage]}%`;
    statusLabel.textContent = STAGES[stageIndex]?.label.toUpperCase() ?? "INITIALIZING";
    statusDetail.textContent = detail ?? STAGES[stageIndex]?.code ?? "准备装载关卡数据";
  }

  function setError(error: unknown, onRetry: () => void, onBack: () => void): void {
    if (disposed) return;
    const message = error instanceof Error ? error.message : String(error);
    root.dataset.state = "error";
    statusLabel.textContent = "LOAD ERROR";
    statusDetail.textContent = "关卡没有进入编辑器";
    errorText.textContent = message;
    errorPanel.hidden = false;
    retryButton.hidden = false;
    loginButton.hidden = true;
    retryButton.onclick = onRetry;
    backButton.onclick = onBack;
  }

  function setAuthRequired(onLogin: () => void, onBack: () => void): void {
    if (disposed) return;
    root.dataset.state = "auth-required";
    statusLabel.textContent = "AUTH REQUIRED";
    statusDetail.textContent = "账号数据需要登录后才能保存";
    errorText.textContent = "请先登录。登录完成后将自动返回铸剑阁，并继续打开当前关卡。";
    errorPanel.hidden = false;
    retryButton.hidden = true;
    loginButton.hidden = false;
    loginButton.onclick = onLogin;
    backButton.onclick = onBack;
  }

  function isMounted(): boolean {
    return !disposed && root.isConnected;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    const elapsed = performance.now() - createdAt;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    if (remaining <= 0) {
      root.remove();
    } else {
      // 不足最小展示时长时，延迟移除 DOM
      // 此期间调用方可能已导航到新页面替换 #app 内容，remove() 静默失败是可接受的
      setTimeout(() => root.remove(), remaining);
    }
  }

  setStage("fetch");
  return { setStage, setError, setAuthRequired, isMounted, dispose };
}
